import { perfMetricsMonitor } from "@/services/monitoring/perf-metrics-monitor/perf-metrics-monitor";
import type {
  MemorySample,
  PerfSnapshot,
  PerfMetricsMonitor,
} from "@/services/monitoring/perf-metrics-monitor/perf-metrics-monitor";
import { isOnSessionPage } from "@/utils/route";
import { captureException } from "@/utils/sentry";

const HEAP_RATIO_THRESHOLD = 0.9;
const SUSTAINED_SAMPLE_COUNT = 5;

type Reload = () => void;

type MemoryWatchdogOptions = {
  perfMetricsMonitor: Pick<PerfMetricsMonitor, "addListener">;
  heapRatioThreshold?: number;
  sustainedSampleCount?: number;
  reload?: Reload;
  startedAt?: number;
};

export class MemoryWatchdog {
  private readonly perfMetricsMonitor: Pick<PerfMetricsMonitor, "addListener">;
  private readonly heapRatioThreshold: number;
  private readonly sustainedSampleCount: number;
  private readonly reload: Reload;
  private readonly startedAt: number;
  private consecutiveOverThreshold = 0;
  private unsubscribePerf: (() => void) | null = null;

  constructor(options: MemoryWatchdogOptions) {
    this.perfMetricsMonitor = options.perfMetricsMonitor;
    this.heapRatioThreshold =
      options.heapRatioThreshold ?? HEAP_RATIO_THRESHOLD;
    this.sustainedSampleCount =
      options.sustainedSampleCount ?? SUSTAINED_SAMPLE_COUNT;
    this.reload = options.reload ?? (() => window.location.reload());
    this.startedAt = options.startedAt ?? Date.now();
  }

  start(): void {
    if (this.unsubscribePerf) {
      return;
    }
    this.unsubscribePerf = this.perfMetricsMonitor.addListener((snapshot) =>
      this.handleSnapshot(snapshot)
    );
  }

  stop(): void {
    this.unsubscribePerf?.();
    this.unsubscribePerf = null;
    this.consecutiveOverThreshold = 0;
  }

  // Never reloads mid-session — an unattended kiosk sitting on the
  // home/setup screens is safe to reload, but yanking an active session
  // would lose in-progress state.
  private handleSnapshot(snapshot: PerfSnapshot): void {
    if (!this.isOverThreshold(snapshot.memory)) {
      this.consecutiveOverThreshold = 0;
      return;
    }

    this.consecutiveOverThreshold += 1;

    if (
      this.consecutiveOverThreshold >= this.sustainedSampleCount &&
      !isOnSessionPage()
    ) {
      this.triggerReload(snapshot.memory);
    }
  }

  private isOverThreshold(memory: MemorySample | null): boolean {
    if (!memory) {
      return false;
    }
    return (
      memory.usedJSHeapSize / memory.jsHeapSizeLimit >= this.heapRatioThreshold
    );
  }

  private triggerReload(memory: MemorySample | null): void {
    this.stop();
    captureException(new Error("Memory pressure watchdog triggered reload"), {
      usedJSHeapSize: memory?.usedJSHeapSize,
      jsHeapSizeLimit: memory?.jsHeapSizeLimit,
      uptimeMs: Date.now() - this.startedAt,
    });
    this.reload();
  }
}

export const memoryWatchdog = new MemoryWatchdog({ perfMetricsMonitor });
